// @owners { team: patients-team }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  Body,
  Button,
  Card,
  Column,
  H3,
  LgPadding,
  ListBase,
  NavBarLogo,
  Row,
  Separator,
  XxsSpacing,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useCallback, useContext, useEffect } from 'react';
import progynyLogo from '~shared/assets/images/progyny_by_alto_logo.png';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { getProgynyWizardFirstFill } from '~shared/selectors/checkout/progyny';
import { getProgynyWizardDosages } from '~shared/selectors/progyny-wizard/getProgynyWizardDosages';
import { useSelectorShared } from '~shared/store';
import { ProgynyPage } from '../../shared/components/ProgynyPage';
import {
  DosageChangeContactProgynyActionSheet,
  DosagesInfoActionSheet,
  ProgynyWizardStepsHeaderContent,
  getProgynyWizardNextRoute,
  useProgynyWizard,
} from '@alto/features';

export const DosageCheck = () => {
  const { getCurrentRouteName, navigate, goBack } = useNavigation();
  const currentRoute = getCurrentRouteName();
  const { trackPageView } = useAnalytics();
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const nextRouteName = getProgynyWizardNextRoute(currentRoute);
  const isRefill = !useSelectorShared(getProgynyWizardFirstFill);
  const { eligiblePrescriptionsInCart } = useProgynyWizard();
  const prescriptionDosages = useSelectorShared(getProgynyWizardDosages);

  const handleShowDosagesInfoActionSheet = useCallback(() => {
    setActiveActionSheet(<DosagesInfoActionSheet />);
  }, [setActiveActionSheet]);

  const onDosageConfirmed = useCallback(() => {
    if (!nextRouteName) return;
    navigate(nextRouteName);
  }, [navigate, nextRouteName]);

  const onDosageChanged = useCallback(() => {
    setActiveActionSheet(<DosageChangeContactProgynyActionSheet />);
  }, [setActiveActionSheet]);

  useEffect(() => {
    trackPageView({
      event: EVENTS.DOSAGE_CHECK_VIEWED,
      params: {
        is_refill: isRefill,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ProgynyPage
      dismissIcon="chevronleft"
      onDismiss={goBack}
      headerBackgroundColor={COLORS.BACKGROUND_COLORS.PINK_LIGHTEST}
      NavBarCenterContent={<NavBarLogo source={progynyLogo} />}
      HeaderContent={
        <ProgynyWizardStepsHeaderContent
          headline={
            eligiblePrescriptionsInCart.length > 1
              ? 'Have there been any updates to your dosages?'
              : 'Has there been any updates to your dosage?'
          }
          subheadline="These are the instructions we have from your provider. If you have more recent dosage updates, our team can assist you with your order."
          linkCopy="Why do you track my dosages?"
          linkOnPress={handleShowDosagesInfoActionSheet}
        />
      }
      buttons={[
        <Button
          key="dosage-changed-button"
          label="Yes, I have a dosage update"
          onPress={onDosageChanged}
          type="tertiary"
        />,
        <Button
          key="dosage-same-button"
          label="No updates, continue"
          onPress={onDosageConfirmed}
          testID="e2e-progyny-dosage-continue-button"
        />,
      ]}
    >
      <Card>
        <ListBase
          separator={<Separator />}
          items={eligiblePrescriptionsInCart.map((prescription) => {
            const dosage = prescriptionDosages.find((dosage) => dosage.prescriptionID === prescription.id);
            return {
              key: prescription.id,
              component: (
                <LgPadding
                  topPadding={SPACING.STATIC.MD}
                  bottomPadding={SPACING.STATIC.MD}
                >
                  <Column
                    flexGrow={1}
                    flexShrink={1}
                  >
                    <H3 fontFamily="semibold">{prescription.medication_name}: </H3>
                    <XxsSpacing />
                    <Row
                      flexGrow={1}
                      flexShrink={1}
                      backgroundColor={COLORS.BACKGROUND_COLORS.WARNING_LIGHTEST}
                    >
                      <Body>{dosage?.instructions || prescription.sig}</Body>
                    </Row>
                  </Column>
                </LgPadding>
              ),
            };
          })}
        />
      </Card>
    </ProgynyPage>
  );
};
